// Realtime subscriptions for the Instructor Management screen. Uses a unique
// random channel topic per mount to avoid the Supabase duplicate-channel crash.
// Callers pass reducers that receive INSERT/UPDATE/DELETE payloads and merge
// them into local state; the hook itself owns no data. Callers should dedupe
// by row id when a direct mutation response and a realtime event race.
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

type RowHandler = (evt: {
  event: "INSERT" | "UPDATE" | "DELETE";
  new: any;
  old: any;
}) => void;

export type InstructorRealtimeHandlers = {
  onInstructor?: RowHandler;
  onContactInfo?: RowHandler;
  onAlias?: RowHandler;
  onAssignment?: RowHandler;
  onOneTimePayment?: RowHandler;
  onUnmatched?: RowHandler;
};

const TABLES: Array<[keyof InstructorRealtimeHandlers, string]> = [
  ["onInstructor", "instructors"],
  ["onContactInfo", "instructor_contact_info"],
  ["onAlias", "instructor_aliases"],
  ["onAssignment", "instructor_class_assignments"],
  ["onOneTimePayment", "instructor_one_time_payments"],
  ["onUnmatched", "instructor_unmatched_references"],
];

export function useInstructorRealtime(handlers: InstructorRealtimeHandlers) {
  // Keep a ref so we don't need to re-subscribe on every handler identity change.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    // Unique topic per mount — Supabase caches channels by topic and a
    // duplicate `.on(...)` on an already-subscribed channel unmounts the tree.
    const topic = `instructor-mgmt-${crypto.randomUUID()}`;
    let channel: RealtimeChannel = supabase.channel(topic);

    for (const [handlerKey, table] of TABLES) {
      channel = channel.on(
        "postgres_changes" as any,
        { event: "*", schema: "public", table },
        (payload: any) => {
          const fn = handlersRef.current[handlerKey];
          if (!fn) return;
          fn({
            event: payload.eventType,
            new: payload.new,
            old: payload.old,
          });
        },
      );
    }
    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
}

// Small helper: idempotent upsert into an array keyed by row.id. Dedupes when a
// mutation response and a realtime event arrive close together.
export function upsertById<T extends { id: string }>(list: T[], row: T): T[] {
  const idx = list.findIndex((r) => r.id === row.id);
  if (idx === -1) return [...list, row];
  const next = list.slice();
  next[idx] = { ...next[idx], ...row };
  return next;
}

export function removeById<T extends { id: string }>(list: T[], id: string): T[] {
  return list.filter((r) => r.id !== id);
}
